import { createContext, useContext, useCallback, useState, ReactNode } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { X } from 'lucide-react'
import { clsx } from 'clsx'

export type ToastType = 'success' | 'error' | 'info' | 'progress'

export interface Toast {
  id: string
  type: ToastType
  title: string
  description?: string
  progress?: number
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  updateToast: (id: string, updates: Partial<Toast>) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID()
    setToasts(prev => [...prev, { ...toast, id }])
    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const updateToast = useCallback((id: string, updates: Partial<Toast>) => {
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, ...updates } : t)))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map(toast => (
          <ToastPrimitive.Root
            key={toast.id}
            className={clsx(
              'bg-zinc-900 border border-zinc-800 rounded-lg p-4 shadow-lg',
              'data-[state=open]:animate-slide-in-right',
              'data-[state=closed]:animate-slide-out-right',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
              'data-[swipe=cancel]:translate-x-0',
              'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
              'min-w-[300px] max-w-[400px]'
            )}
            duration={toast.type === 'error' || toast.type === 'progress' ? Infinity : (toast.duration || 5000)}
            onOpenChange={(open) => {
              if (!open) removeToast(toast.id)
            }}
          >
            <div className="flex items-start gap-3">
              <ToastIcon type={toast.type} />
              <div className="flex-1">
                <ToastPrimitive.Title className="font-medium text-white">
                  {toast.title}
                </ToastPrimitive.Title>
                {toast.description && (
                  <ToastPrimitive.Description className="text-sm text-zinc-400 mt-1">
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
                {toast.type === 'progress' && toast.progress !== undefined && (
                  <div className="mt-2">
                    <div className="bg-zinc-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-500 h-full transition-all duration-300"
                        style={{ width: `${toast.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-zinc-500 mt-1">{toast.progress}%</span>
                  </div>
                )}
                {toast.action && (
                  <ToastPrimitive.Action
                    altText={toast.action.label}
                    onClick={toast.action.onClick}
                    className="mt-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {toast.action.label}
                  </ToastPrimitive.Action>
                )}
              </div>
              <ToastPrimitive.Close className="text-zinc-500 hover:text-white transition-colors">
                <X size={16} />
              </ToastPrimitive.Close>
            </div>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 flex flex-col gap-2 z-40 max-h-screen" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

function ToastIcon({ type }: { type: ToastType }) {
  const className = "w-5 h-5"
  switch (type) {
    case 'success':
      return (
        <div className={clsx(className, 'text-green-500')}>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      )
    case 'error':
      return (
        <div className={clsx(className, 'text-red-500')}>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
      )
    case 'progress':
      return (
        <div className={clsx(className, 'text-blue-500 animate-spin')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.364 6.364l-2.828-2.828M9.464 9.464L6.636 6.636m12.728 0l-2.828 2.828M9.464 14.536l-2.828 2.828" />
          </svg>
        </div>
      )
    default:
      return (
        <div className={clsx(className, 'text-blue-500')}>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
      )
  }
}
